import styled from "@emotion/styled/macro";
import {
  Button,
  Modal,
  tokens,
  Typography,
  LoadingIndicator,
  Tooltip,
  ErrorModal as LibErrorModal,
} from "@sunrun/experience-ui-components";
import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import type {
  Contact,
  Role,
} from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import { useMediaQuery } from "react-responsive";
import { CheckoutSnackbar } from "../atoms/CheckoutSnackbar";
import { StatusChip } from "../atoms/StatusChip";
import { SlimContactCard } from "../molecules/SlimContactCard";
import { SplatContact } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { ReadSignDocsStep } from "../../hooks/useReadSignDocs";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardSectionDivider,
} from "../molecules/Card";
import { ConfirmModal } from "../molecules/ConfirmModal";
import { DuplicateValidationWarningBanner } from "../molecules/DuplicateValidationWarningBanner";
import { ROLES } from "utils/usageUtils";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import { useCheckoutDocViewing } from "checkout/hooks/useCheckoutDocViewing";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { ErrorModal } from "components/molecules/ErrorModal";
import { isExpiredAuthTokenError } from "utils/errorUtils";
import {
  sendIpaEmail,
  sendLoanEmail,
  signDocument,
  voidDocument,
} from "services/prospect";
import { useSendDocumentSMS } from "checkout/hooks/useSendDocumentSMS";
import { useContactManagement } from "hooks/useContactManagement";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { STATUS_CHIP_STATUSES } from "checkout/util/constants";
import { isKnownDocumentCardError } from "checkout/util/documentCommonErrors";
import { useAuthToken } from "hooks/useAuthToken";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import {
  CUSTOMER_SELF_CHECKOUT__CLICKED_EMAIL_DOCUMENT_IN_MODAL_CHECKOUT,
  CUSTOMER_SELF_CHECKOUT__COMBINED_SIGN_ERROR,
  SALES_REP_CHECKOUT__COMBINED_SIGN_ERROR,
} from "constants/mixpanelEvents";
import { Mixpanel } from "providers/mixpanel";
import { useDocumentStatus } from "checkout/hooks/useDocumentStatus";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import { isNemAgreementCheckMessage } from "utils/contractReviewUtils";

type DocumentCardProps = Omit<
  ReadSignDocsStep,
  "prerequisites" | "isRequired" | "odiName" | "documentType"
> & {
  primaryContact: SplatContact;
  secondaryContact?: SplatContact;
  setSecondaryContact?: (contact: SplatContact | undefined) => void;
  refreshTasks: () => Promise<any>;
  odiName?: string;
  refreshCombinedEnvelopeStatus?: () => void;
  isCombinedAgreement?: boolean;
  inProgress?: boolean;
  hideCardSectionDivider?: boolean;
  hideContactSection?: boolean;
  hideStatusChip?: boolean;
  cardDataTestId?: string;
  documentType: ReadSignDocsStep["documentType"] | "Combined Docs";
  lockedTooltip?: string;
};

const testIds = {
  SECONDARY_SIGNER_ADD:
    "offer-experience--DocumentCard--secondaryContactDocumentCard--add--button",
  SECONDARY_SIGNER_CHANGE:
    "offer-experience--DocumentCard--secondaryContactDocumentCard--change--button",
  SECONDARY_SIGNER_REMOVE:
    "offer-experience--DocumentCard--secondaryContactDocumentCard--remove--button",
};

type DocusignType = "EMAIL" | "INPERSON" | "SMS";

const DocumentCard = ({
  odiName,
  docName,
  isUnlocked,
  completed,
  description,
  allowSecondaryContact,
  primarySignerRole,
  primaryContact,
  secondaryContact,
  documentType,
  showRead,
  showSMS,
  showEmail,
  showSign,
  disableEmail,
  isOptional,
  refreshTasks,
  setSecondaryContact,
  refreshCombinedEnvelopeStatus,
  isCombinedAgreement = false,
  inProgress = false,
  hideCardSectionDivider = false,
  hideContactSection = false,
  hideStatusChip = false,
  cardDataTestId,
  lockedTooltip,
}: DocumentCardProps) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const authKey = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [sendSMSModal, setSendSMSModal] = useState(false);
  const [documentActionError, setDocumentActionError] = useState(false);
  const [documentCardError, setDocumentCardError] = useState<Error>();
  const [documentActionErrorMessage, setDocumentActionErrorMessage] =
    useState("");
  const [documentActionErrorFunction, setDocumentActionErrorFunction] =
    useState<
      | undefined
      | "SendSMS"
      | "SendEmail"
      | "SendIPAEmail"
      | "SignInPerson"
      | "VoidDocument"
    >();
  const [removeSecondarySignerError, setRemoveSecondarySignerError] = useState<{
    action?: string;
    error?: Error;
  }>();
  const [confirmVoidModal, setConfirmVoidModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [loadingSendEmail, setLoadingSendEmail] = useState(false);
  const [loadingInPerson, setLoadingInPerson] = useState(false);
  const [loadingVoidDocument, setLoadingVoidDocument] = useState(false);
  const newDocusignType = useRef<DocusignType>("EMAIL");
  const oldDocusignType = useRef<DocusignType>("EMAIL");
  const hasSecondaryContact = secondaryContact && allowSecondaryContact;
  const {
    prospectId,
    proposalId,
    navigateToTask,
    userProfile,
    selectedProposal,
    contractReviewMessages,
  } = useCheckoutContext();
  const { viewDocument, viewDocLoading, viewDocError, resetError } =
    useCheckoutDocViewing(prospectId, proposalId, documentType);
  const [showSecondaryModal, setShowSecondaryModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showIpaSuccessSnackbar, setShowIpaSuccessSnackbar] = useState(false);

  const {
    data: documentStatus,
    error: documentStatusError,
    isLoading: documentStatusLoading,
  } = useDocumentStatus(
    isUnlocked,
    prospectId,
    proposalId,
    documentType === "Combined Docs" ? undefined : documentType
  );

  const status: keyof typeof STATUS_CHIP_STATUSES = useMemo(() => {
    if (isUnlocked) {
      if (documentStatusError) {
        return "error";
      }
      if (!documentStatus || documentStatus?.documentStatus === "uninitiated") {
        return "readyToSign";
      }
      if (
        documentStatus?.documentStatus === "sr_approved" ||
        documentStatus?.documentStatus === "submitted" ||
        documentStatus?.documentStatus === "wet_signed"
      ) {
        return "signed";
      }
      if (documentStatus?.recipients.length > 0) {
        const primarySigner = documentStatus.recipients.find(
          (recipient) => recipient.routingOrder === 1
        );
        if (primarySigner?.status !== "Completed") {
          return "pendingCustomerSignature";
        }

        // The second signer (if present) is either the sales rep or a secondary signer
        const nextSigner = documentStatus.recipients.find(
          (recipient) => recipient.routingOrder === 2
        );
        if (nextSigner && nextSigner?.status !== "Completed") {
          if (nextSigner.email === selectedProposal?.salesRepEmail1) {
            return "pendingSalesRep";
          }
          return "pendingSecondarySigner";
        }

        // The third signer (if present) is the sales rep when a secondary signer is present, otherwise it's Sunrun Ops (which can be ignored)
        const thirdSigner = documentStatus.recipients.find(
          (recipient) => recipient.routingOrder === 3
        );
        if (
          thirdSigner &&
          thirdSigner.email === selectedProposal?.salesRepEmail1 &&
          thirdSigner?.status !== "Completed"
        ) {
          return "pendingSalesRep";
        }

        return "signed";
      }

      // Not in a status we recognize and no recipients, fallback to readyToSign
      return "readyToSign";
    }
    return "locked";
  }, [isUnlocked, documentStatusError, documentStatus, selectedProposal]);

  const {
    sendSMS: handleSendSMS,
    loading: sendSMSLoading,
    success: sendSMSSuccess,
    successMessage: sendSMSSuccessMessage,
    closeSuccessMessage: closeSendSMSSuccessMessage,
    error: sendSMSError,
  } = useSendDocumentSMS({
    authKey: authKey || proposalToken || "",
    prospectId,
    proposalId,
    documentType: documentType === "Combined Docs" ? undefined : documentType,
    primaryContactId: primaryContact.contactId,
    secondaryContactId: secondaryContact?.contactId,
    isCustomer: !!proposalToken,
    docName,
    combinedAgreement: isCombinedAgreement,
  });

  useEffect(() => {
    if (sendSMSSuccess) {
      setSendSMSModal(false);
    }
    if (sendSMSError) {
      setSendSMSModal(false);
      setDocumentCardError(sendSMSError);
      setDocumentActionErrorMessage(
        "Sorry, we couldn't send your text message."
      );
      setDocumentActionErrorFunction("SendSMS");
      setDocumentActionError(true);
    }
  }, [sendSMSSuccess, sendSMSError]);

  const toggleSendSMSModal = () => setSendSMSModal((prev) => !prev);
  const toggleSendEmailModal = () => setSendEmailModal((prev) => !prev);
  const toggleConfirmVoidModal = () => setConfirmVoidModal((prev) => !prev);
  const toggleInfoModal = () => setShowInfoModal((prev) => !prev);

  const { onEditContactRole } = useContactManagement();

  const handleSendEmail = useCallback(async () => {
    if (isCustomer) {
      Mixpanel.track(
        CUSTOMER_SELF_CHECKOUT__CLICKED_EMAIL_DOCUMENT_IN_MODAL_CHECKOUT,
        {
          prospectId,
          documentType,
          docName,
          proposalId,
        }
      );
    }
    setLoadingSendEmail(true);
    try {
      if (docName === "Illinois Power Authority") {
        await sendIpaEmail(
          authKey || proposalToken || "",
          prospectId,
          proposalId,
          !!proposalToken
        );
      } else if (docName === "Loan") {
        await sendLoanEmail(
          authKey || proposalToken || "",
          prospectId,
          proposalId,
          secondaryContact?.contactId,
          !!proposalToken
        );
      } else {
        const sendEmailSecondaryContact = odiName !== "cpuc";
        await signDocument(
          {
            authKey: authKey || proposalToken || "",
            prospectId,
            proposalId,
            documentType:
              documentType === "Combined Docs" ? undefined : documentType,
            eSignatureSource: "Sales Platform",
            eSignatureType: "EMAIL",
            primaryContactId: primaryContact.contactId,
            ...(isCombinedAgreement ? { combinedAgreement: true } : {}),
          },
          sendEmailSecondaryContact ? secondaryContact?.contactId : undefined,
          undefined,
          !!proposalToken
        );
        refreshCombinedEnvelopeStatus?.();
      }

      if (docName === "Illinois Power Authority") {
        setShowIpaSuccessSnackbar(true);
      } else {
        setShowSuccessSnackbar(true);
      }
      setDocumentActionError(false);
    } catch (error: any) {
      if (isCombinedAgreement) {
        Mixpanel.track(
          isCustomer
            ? CUSTOMER_SELF_CHECKOUT__COMBINED_SIGN_ERROR
            : SALES_REP_CHECKOUT__COMBINED_SIGN_ERROR,
          {
            prospectId,
            documentType,
            docName,
            proposalId,
            error: error.message,
          }
        );
      }
      setDocumentActionErrorMessage(
        docName === "Illinois Power Authority"
          ? "Something went wrong in sending the IPA document"
          : "Sorry, we couldn't send your email."
      );
      setDocumentActionErrorFunction(
        docName === "Illinois Power Authority" ? "SendIPAEmail" : "SendEmail"
      );
      setDocumentActionError(true);
      setDocumentCardError(error);
    } finally {
      setSendEmailModal(false);
      setLoadingSendEmail(false);
    }
  }, [
    isCustomer,
    prospectId,
    documentType,
    docName,
    proposalId,
    authKey,
    proposalToken,
    secondaryContact?.contactId,
    odiName,
    primaryContact.contactId,
    isCombinedAgreement,
    refreshCombinedEnvelopeStatus,
  ]);

  const handleSignInPerson = useCallback(async () => {
    try {
      const signResponse: any = await signDocument(
        {
          authKey: authKey || proposalToken || "",
          prospectId,
          proposalId,
          documentType:
            documentType === "Combined Docs" ? undefined : documentType,
          eSignatureSource: "Sales Platform",
          eSignatureType: "INPERSON",
          primaryContactId: primaryContact.contactId,
        },
        secondaryContact?.contactId,
        undefined,
        !!proposalToken
      );
      refreshCombinedEnvelopeStatus?.();
      const docusignWindow = window.open(signResponse.url, "_blank");
      setLoadingInPerson(false);

      const timer = setInterval(async () => {
        if (docusignWindow && docusignWindow.closed) {
          clearInterval(timer);
          await refreshTasks();
        }
      }, 1000);
      setDocumentActionError(false);
    } catch (error: any) {
      setDocumentCardError(error);
      setDocumentActionErrorMessage("Sorry, we couldn't sign your document.");
      setDocumentActionErrorFunction("SignInPerson");
      setDocumentActionError(true);
    } finally {
      setLoadingInPerson(false);
    }
  }, [
    authKey,
    proposalToken,
    prospectId,
    proposalId,
    documentType,
    primaryContact.contactId,
    secondaryContact?.contactId,
    refreshCombinedEnvelopeStatus,
    refreshTasks,
  ]);

  const updateSecondaryContact = useCallback(
    () => navigateToTask("confirmNames", { secondaryEdit: true }),
    [navigateToTask]
  );

  const handleCheckDocumentStatus = useCallback(async () => {
    if (newDocusignType.current === "INPERSON") {
      setLoadingInPerson(true);
    }
    if (
      documentStatus?.documentStatus !== "uninitiated" &&
      documentStatus?.documentStatus !== "voided" &&
      !isCombinedAgreement
    ) {
      oldDocusignType.current =
        (documentStatus?.eSignType as DocusignType) ?? newDocusignType.current;
      toggleConfirmVoidModal();
      return;
    }
    if (inProgress) {
      toggleInfoModal();
      return;
    }
    switch (newDocusignType.current) {
      case "SMS":
        toggleSendSMSModal();
        break;
      case "EMAIL":
        toggleSendEmailModal();
        break;
      case "INPERSON":
        handleSignInPerson();
        break;
      default:
        throw new Error("Unexpected docusign type");
    }
    setDocumentActionError(false);
  }, [
    documentStatus?.documentStatus,
    documentStatus?.eSignType,
    handleSignInPerson,
    inProgress,
    isCombinedAgreement,
  ]);

  const voidAndReInitiateDocusign = useCallback(async () => {
    if (documentType === "Combined Docs") {
      return;
    }

    setLoadingVoidDocument(true);
    try {
      await voidDocument({
        authKey: authKey || proposalToken || "",
        prospectId,
        proposalId,
        documentType,
        eSignatureSource: "Sales Platform",
        eSignatureType: oldDocusignType.current,
        primaryContactId: primaryContact.contactId,
        isCustomer: !!proposalToken,
      });

      switch (newDocusignType.current) {
        case "SMS":
          await handleSendSMS();
          break;
        case "EMAIL":
          await handleSendEmail();
          break;
        case "INPERSON":
          await handleSignInPerson();
          break;
        default:
          console.log("Unexpected docusign type");
          break;
      }
      setDocumentActionError(false);
    } catch (error: any) {
      setDocumentCardError(error);
      setDocumentActionErrorMessage("Sorry, we couldn't Void the Document.");
      setDocumentActionErrorFunction("VoidDocument");
      setDocumentActionError(true);
    } finally {
      setLoadingVoidDocument(false);
      setConfirmVoidModal(false);
    }
  }, [
    authKey,
    proposalToken,
    prospectId,
    proposalId,
    documentType,
    primaryContact.contactId,
    handleSendSMS,
    handleSendEmail,
    handleSignInPerson,
  ]);

  const showKnownDocumentError =
    documentActionError && isKnownDocumentCardError(documentCardError);

  const handleDocuments = useCallback(
    async (docusignType: DocusignType) => {
      newDocusignType.current = docusignType;
      await handleCheckDocumentStatus();
    },
    [handleCheckDocumentStatus]
  );

  const handleRetryFunction = async () => {
    switch (documentActionErrorFunction) {
      case "SendSMS":
        await handleSendSMS();
        refreshCombinedEnvelopeStatus?.();
        break;
      case "SendEmail":
        handleSendEmail();
        break;
      case "SendIPAEmail":
        handleSendEmail();
        break;
      case "SignInPerson":
        handleSignInPerson();
        break;
      case "VoidDocument":
        voidAndReInitiateDocusign();
    }
  };
  const handleRemoveSecondarySigner = useCallback(
    async (contact: Contact, roleToUpdate: Role, newRole: Role) => {
      try {
        // Update ROLE of the contact
        await onEditContactRole({ ...contact, roleToUpdate, newRole });
      } catch (error) {
        setRemoveSecondarySignerError({
          action: "Removing Secondary Contact",
          error: error as Error,
        });
      }
    },
    [onEditContactRole]
  );

  const primaryContactLabel = {
    [ROLES.HOMEOWNER]: "Primary signer",
    [ROLES.UTILITYBILLCONTACT]: "Utility Bill Contact",
  }[primarySignerRole];

  const sendActionDisabled =
    !isUnlocked || disableEmail || documentStatusLoading;

  const renderCardFooter = useMemo(() => {
    return (
      <CardFooter>
        <>
          {isMobile ? (
            <>
              <ActionWrapper isMobile={isMobile}>
                {showRead && completed && (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={!isUnlocked || viewDocLoading}
                    size="sm"
                    color="secondary"
                    onClick={viewDocument}
                  >
                    {viewDocLoading ? (
                      <LoadingIndicator
                        height={25}
                        width={25}
                        showLoadingMessage={false}
                        color="black"
                      />
                    ) : (
                      "View signed document"
                    )}
                  </StyledButton>
                )}
                {!completed &&
                  showSMS &&
                  userProfile &&
                  isSunrunManaged(userProfile) && (
                    <StyledButton
                      isMobile={isMobile}
                      disabled={sendActionDisabled}
                      onClick={() => handleDocuments("SMS")}
                      size="sm"
                      color={showSign ? "secondary" : "primary"}
                      data-testid={
                        isCombinedAgreement
                          ? "send-all-documents-text-message"
                          : "text-message-button"
                      }
                    >
                      {documentStatusLoading ? "Loading" : "Text message"}
                    </StyledButton>
                  )}
                {!completed && showEmail && (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={sendActionDisabled}
                    onClick={() => handleDocuments("EMAIL")}
                    size="sm"
                    color={showSign ? "secondary" : "primary"}
                    data-testid={
                      isCombinedAgreement
                        ? "send-all-documents-email"
                        : "email-button"
                    }
                  >
                    {documentStatusLoading ? "Loading" : "Email"}
                  </StyledButton>
                )}
                {!completed && showSign && (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={!isUnlocked || loadingInPerson}
                    size="sm"
                    onClick={() => handleDocuments("INPERSON")}
                  >
                    {loadingInPerson ? "Loading" : "Sign"}
                  </StyledButton>
                )}
                {showRead && !completed && (
                  <HyperlinkButton
                    disabled={viewDocLoading}
                    onClick={viewDocument}
                  >
                    Read
                  </HyperlinkButton>
                )}
              </ActionWrapper>
            </>
          ) : (
            <>
              {showRead && !completed ? (
                <HyperlinkButton
                  disabled={viewDocLoading}
                  onClick={viewDocument}
                >
                  Read
                </HyperlinkButton>
              ) : (
                // Layout element to shift ActionWrapper to the right
                <div />
              )}
              <ActionWrapper>
                {showRead && completed && (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={!isUnlocked || viewDocLoading}
                    size="sm"
                    color="secondary"
                    onClick={viewDocument}
                  >
                    {viewDocLoading ? (
                      <LoadingIndicator
                        height={25}
                        width={25}
                        showLoadingMessage={false}
                        color="black"
                      />
                    ) : (
                      "View signed document"
                    )}
                  </StyledButton>
                )}
                {!completed &&
                  showSMS &&
                  userProfile &&
                  isSunrunManaged(userProfile) && (
                    <StyledButton
                      isMobile={isMobile}
                      disabled={sendActionDisabled}
                      onClick={() => handleDocuments("SMS")}
                      size="sm"
                      color={showSign ? "secondary" : "primary"}
                      data-testid={
                        isCombinedAgreement
                          ? "send-all-documents-text-message"
                          : "email-button"
                      }
                    >
                      {documentStatusLoading ? "Loading" : "Text message"}
                    </StyledButton>
                  )}
                {!completed && showEmail && (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={sendActionDisabled}
                    onClick={() => handleDocuments("EMAIL")}
                    size="sm"
                    color={showSign ? "secondary" : "primary"}
                    data-testid={
                      isCombinedAgreement
                        ? "send-all-documents-email"
                        : "email-button"
                    }
                  >
                    {documentStatusLoading ? "Loading" : "Email"}
                  </StyledButton>
                )}
                {!completed && showSign && (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={!isUnlocked || loadingInPerson}
                    size="sm"
                    onClick={() => handleDocuments("INPERSON")}
                  >
                    {loadingInPerson ? "Loading" : "Sign"}
                  </StyledButton>
                )}
              </ActionWrapper>
            </>
          )}
        </>
      </CardFooter>
    );
  }, [
    completed,
    isMobile,
    showRead,
    isUnlocked,
    viewDocLoading,
    viewDocument,
    showSMS,
    userProfile,
    sendActionDisabled,
    showSign,
    isCombinedAgreement,
    documentStatusLoading,
    showEmail,
    loadingInPerson,
    handleDocuments,
  ]);

  return (
    <>
      <CheckoutSnackbar
        open={!!sendSMSSuccessMessage}
        autoHideDuration={4000}
        colorScheme="light"
        message={sendSMSSuccessMessage}
        onClose={closeSendSMSSuccessMessage}
        type="success"
      />
      <CheckoutSnackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        colorScheme="light"
        message="Email sent successfully!"
        onClose={() => setShowSuccessSnackbar(false)}
        type="success"
      />
      <CheckoutSnackbar
        open={showIpaSuccessSnackbar}
        autoHideDuration={4000}
        colorScheme="light"
        message="Request sent, may take 3 mins to appear in your inbox."
        onClose={() => setShowIpaSuccessSnackbar(false)}
        type="success"
      />
      {showSecondaryModal && (
        <Modal
          testId="checkout-remove-secondary-signer-modal"
          type="warning"
          onClose={() => {
            setShowSecondaryModal(false);
          }}
          title="Are you sure you want to remove this signer?"
          primaryButton={{
            text: "Remove secondary signer",
            onClick: async () => {
              await handleRemoveSecondarySigner(
                secondaryContact!,
                ROLES.CONTRACTCOSIGNER,
                ROLES.OTHER
              );
              setSecondaryContact!(undefined);
              setShowSecondaryModal(false);
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setShowSecondaryModal(false),
          }}
        >
          <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
            If you remove the secondary signer, only{" "}
            {primaryContact.customerFirstName} {primaryContact.customerLastName}{" "}
            will be able to sign.
          </Typography>
        </Modal>
      )}
      <Card data-testid={cardDataTestId}>
        <StyledDocumentCardHeader
          isMobile={isMobile}
          smallBottomMargin={hideCardSectionDivider}
        >
          <HeaderTitle>
            <Typography
              size={tokens.FONT_SIZE_3}
              color={tokens.BRAND_HEROBLUE}
              fontWeight={isMobile ? "medium" : "regular"}
            >
              {docName}
            </Typography>
            {isOptional && (
              <Typography
                size={tokens.FONT_SIZE_3}
                color={tokens.TINTS_OFF_WHITE_20}
                data-testid="checkout-read-and-sign-net-task-metering-header"
              >
                Optional
              </Typography>
            )}
          </HeaderTitle>
          {!hideStatusChip && (
            <StatusChip status={status} isMobile={isMobile} />
          )}
        </StyledDocumentCardHeader>
        {!hideCardSectionDivider && <CardSectionDivider />}
        <CardContent smallTopMargin={hideCardSectionDivider}>
          {odiName === "nemAgreement" && (
            <>
              <ContractReviewBanners
                messages={contractReviewMessages}
                category="nem_agreement_check"
                filter={(message) => !isNemAgreementCheckMessage(message)}
                sessionDismissScopeId={proposalId}
              />
              <ContractReviewBanners
                messages={contractReviewMessages}
                category="nem_agreement_check"
                filter={(message) => isNemAgreementCheckMessage(message)}
                actionLink={{
                  text: "Edit signers",
                  onClick: () => navigateToTask("confirmNames"),
                }}
                sessionDismissScopeId={proposalId}
              />
            </>
          )}
          <Typography variant="p" color={tokens.TINTS_OFF_WHITE_20}>
            {description}
          </Typography>
          {odiName === "customerAgreement" && (
            <DuplicateValidationWarningBanner
              validationStatus={primaryContact.validationStatus}
              onViewDetails={() => navigateToTask("confirmNames")}
            />
          )}
          {!hideContactSection && (
            <>
              {docName === "Loan" && (
                <SlimContactCard
                  contact={primaryContact}
                  secondaryContact={secondaryContact}
                  label={secondaryContact ? "Borrowers" : "Borrower"}
                />
              )}
              {docName !== "Loan" && (
                <>
                  <SlimContactCard
                    contact={primaryContact}
                    label={primaryContactLabel}
                  />

                  {hasSecondaryContact && (
                    <SlimContactCard
                      contact={secondaryContact!}
                      label="Secondary signer"
                    >
                      {!completed && (
                        <SecondarySignerActions>
                          <HyperlinkButton
                            data-testid={testIds.SECONDARY_SIGNER_REMOVE}
                            onClick={() => setShowSecondaryModal(true)}
                          >
                            Remove
                          </HyperlinkButton>
                          <HyperlinkButton
                            onClick={updateSecondaryContact}
                            data-testid={testIds.SECONDARY_SIGNER_CHANGE}
                          >
                            Edit
                          </HyperlinkButton>
                        </SecondarySignerActions>
                      )}
                    </SlimContactCard>
                  )}

                  {allowSecondaryContact &&
                    !hasSecondaryContact &&
                    !completed && (
                      <div style={{ alignSelf: "flex-start" }}>
                        <StyledButton
                          isMobile={isMobile}
                          color="secondary"
                          size="sm"
                          onClick={updateSecondaryContact}
                        >
                          Add Secondary Signer
                        </StyledButton>
                      </div>
                    )}
                </>
              )}
            </>
          )}
        </CardContent>
        {isUnlocked || !lockedTooltip ? (
          renderCardFooter
        ) : (
          <Tooltip content={lockedTooltip} direction="top-right">
            {renderCardFooter}
          </Tooltip>
        )}
        {showKnownDocumentError && (
          <LibErrorModal
            action={documentActionErrorFunction}
            testId={`checkout-documents-${documentActionErrorFunction}`}
            error={documentCardError}
            onClose={() => setDocumentActionError(false)}
            primaryButton={{
              text: "Retry",
              onClick: () => {
                handleRetryFunction();
                setDocumentActionError(false);
              },
              testId: `checkout-documents-${documentActionErrorFunction}-acknowledge-button`,
              ariaLabel: `checkout-documents-${documentActionErrorFunction}-acknowledge-button`,
              disabled: false,
              isLoading: false,
            }}
            secondaryButton={{
              text: "Dismiss",
              onClick: () => setDocumentActionError(false),
              testId: `checkout-documents-${documentActionErrorFunction}-dismiss-button`,
              ariaLabel: `checkout-documents-${documentActionErrorFunction}-dismiss-button`,
              disabled: false,
              isLoading: false,
            }}
          />
        )}

        {viewDocError && (
          <ErrorModal
            action="Viewing document"
            context="checkout-documents-read"
            error={viewDocError}
            onClose={resetError}
            onRetry={viewDocument}
            severity={
              isExpiredAuthTokenError(viewDocError) ? "warning" : "error"
            }
          />
        )}
        {removeSecondarySignerError && (
          <ErrorModal
            action={removeSecondarySignerError.action}
            context="checkout-documents-remove-secondary-signer"
            error={removeSecondarySignerError.error}
            onClose={() => setRemoveSecondarySignerError(undefined)}
          />
        )}

        {showInfoModal && (
          <Modal
            testId="checkout-info-modal"
            title="Already sent for signature"
            type="info"
            onClose={toggleInfoModal}
            primaryButton={{ text: "Close", onClick: toggleInfoModal }}
          >
            <Content>
              <Typography variant="p">
                This document has already been sent for signature. Please check
                the email inbox or phone message.
              </Typography>
            </Content>
          </Modal>
        )}
        {confirmVoidModal && (
          <ConfirmModal
            title="Are you sure you want to re-start signing for this agreement?"
            confirmLabel="Void & Proceed"
            onClose={toggleConfirmVoidModal}
            onConfirm={voidAndReInitiateDocusign}
            loading={loadingVoidDocument}
          >
            <Content>
              <Typography variant="p">
                This action will void the Docusign Envelope for signing the{" "}
                {docName} that has already been initiated.
              </Typography>
            </Content>
          </ConfirmModal>
        )}
        {sendEmailModal && (
          <ConfirmModal
            title="Send Document"
            confirmLabel="Send Email"
            onClose={toggleSendEmailModal}
            onConfirm={handleSendEmail}
            loading={loadingSendEmail}
          >
            <Content>
              <Typography variant="p">
                Your {documentType} will be sent to:
              </Typography>
              <List>
                {primaryContact.customerEmail && (
                  <li>
                    <Typography variant="p">
                      {primaryContact.customerEmail}
                    </Typography>
                  </li>
                )}
                {allowSecondaryContact && secondaryContact?.customerEmail && (
                  <li>
                    <Typography variant="p">
                      {secondaryContact.customerEmail}
                    </Typography>
                  </li>
                )}
              </List>
            </Content>
          </ConfirmModal>
        )}
        {sendSMSModal && (
          <ConfirmModal
            title="Text Document"
            confirmLabel="Send text message"
            onClose={toggleSendSMSModal}
            onConfirm={async () => {
              await handleSendSMS();
              refreshCombinedEnvelopeStatus?.();
            }}
            loading={sendSMSLoading}
            additionalConfirmation="Customer gave verbal consent to receive this text message"
            hideIcon={true}
          >
            <Content>
              <Typography variant="p">
                The document text message will be sent to the phone number
                below.
              </Typography>
              <SMSContactWrapper>
                <Typography
                  color={tokens.BRAND_HERO_BLUE}
                  size={tokens.FONT_SIZE_3}
                  testId="slim-contact-card-label"
                >
                  {primaryContact.customerFirstName}{" "}
                  {primaryContact.customerLastName}
                </Typography>

                <Typography
                  color={tokens.TINTS_OFF_WHITE_20}
                  size={tokens.FONT_SIZE_3}
                  testId="slim-contact-card-contact-names"
                >
                  +1 {primaryContact.customerPrimaryPhone}
                </Typography>
              </SMSContactWrapper>
            </Content>
          </ConfirmModal>
        )}
        {documentActionError && !showKnownDocumentError && (
          <ConfirmModal
            title={documentActionErrorMessage}
            confirmLabel="Retry"
            onClose={() => setDocumentActionError(false)}
            onConfirm={handleRetryFunction}
            loading={loadingSendEmail || loadingVoidDocument || sendSMSLoading}
            modalType={"error"}
          >
            <>
              {documentActionErrorFunction === "SendIPAEmail" && (
                <Typography variant="p">
                  Please text Sales Support Pre SRA at{" "}
                  <a href="tel:385-317-0193">385-317-0193</a> for help
                  completing the Illinois Power Authority (IPA) document.
                </Typography>
              )}
              <ErrorComponent
                error={documentCardError}
                context="DocumentCard"
                action={documentActionErrorFunction ?? "Unknown"}
              />
            </>
          </ConfirmModal>
        )}
      </Card>
    </>
  );
};

const StyledButton = styled(Button)<{ isMobile?: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

const StyledDocumentCardHeader = styled(CardHeader)<{ isMobile?: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    margin: 24px;
    align-items: flex-start;
  `}
`;

const SecondarySignerActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ActionWrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 16px;

  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
    flex-direction: column;
    align-items: center;
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const List = styled.ul`
  margin: 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  gap: 16px;
`;

const SMSContactWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 8px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  background: ${tokens.TINTS_OFF_WHITE_70};
  margin-bottom: 16px;
`;

export { DocumentCard, testIds };
export type { DocumentCardProps };
